/* eslint-disable react-refresh/only-export-components */
import { Component, type ErrorInfo, type ReactNode } from "react";
import {
  classifyAppError,
  reloadForStaleChunkOnce,
  type AppErrorCategory,
} from "./error-classification";
import { reportResilienceState } from "./resilience-store";

type Props = { readonly children: ReactNode };
type State = {
  readonly caught: boolean;
  readonly category: AppErrorCategory;
};

const DEFAULT_STATE: State = {
  caught: false,
  category: "unknown",
};

export class AppErrorBoundary extends Component<Props, State> {
  public state: State = DEFAULT_STATE;
  private lastStableMarkup: string | null = null;
  private resetTimer: number | null = null;

  public static getDerivedStateFromError(error: unknown): State {
    const classified = classifyAppError(error);
    return { caught: true, category: classified.category };
  }

  public componentDidCatch(error: Error, info: ErrorInfo): void {
    // Stale lazy chunk after a deploy (e.g. opening a trade 404s the
    // TradeCardMiniChart chunk): a reload — not a re-render of the same failing
    // tree — is the only recovery. Reload once, then skip the normal flow.
    if (reloadForStaleChunkOnce(error)) return;
    const classified = classifyAppError(error);
    reportBoundaryState(classified.category, classified.code, info);
    this.scheduleAutoReset();
  }

  public componentDidMount(): void {
    if (!this.state.caught) this.captureStableMarkup();
  }

  public componentDidUpdate(): void {
    if (!this.state.caught) {
      this.captureStableMarkup();
      return;
    }
    this.scheduleAutoReset();
  }

  public componentWillUnmount(): void {
    if (this.resetTimer !== null) window.clearTimeout(this.resetTimer);
  }

  private handleReset = (): void => {
    if (this.resetTimer !== null) window.clearTimeout(this.resetTimer);
    this.resetTimer = null;
    this.setState(DEFAULT_STATE);
  };

  private scheduleAutoReset(): void {
    if (this.resetTimer !== null) return;
    this.resetTimer = window.setTimeout(this.handleReset, 1_500);
  }

  private captureStableMarkup(): void {
    // User-reported DEV UX bug: never replace the app with support-code copy
    // during transient deploy/runtime faults; keep the last good screen visible.
    const root = document.getElementById("root");
    this.lastStableMarkup = root?.innerHTML ?? this.lastStableMarkup;
  }

  public render(): ReactNode {
    if (!this.state.caught) return this.props.children;
    return <LastStableScreen html={this.lastStableMarkup} />;
  }
}

function LastStableScreen(props: {
  readonly html: string | null;
}): React.JSX.Element {
  if (props.html === null) {
    return (
      <div
        aria-hidden="true"
        className="min-h-dvh bg-background"
        data-testid="app-last-stable-fallback"
      />
    );
  }
  return (
    <div
      aria-hidden="true"
      data-testid="app-last-stable-fallback"
      dangerouslySetInnerHTML={{ __html: props.html }}
    />
  );
}

function reportBoundaryState(
  category: AppErrorCategory,
  code: string,
  info: ErrorInfo,
): void {
  const reportedCode = category === "unknown" ? null : code;
  reportResilienceState(
    category === "network-offline" ? "offline" : "reconnecting",
    reportedCode,
  );
  console.warn(
    JSON.stringify({
      event: "app_error_boundary",
      category,
      code: reportedCode,
      componentStack: info.componentStack,
    }),
  );
}
